<script>
  import { onMount } from 'svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSave, faRotate } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { user, hasRole } from '$stores/sessionStore';
  import Toolbar from '$lib/Toolbar.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';

  const API_BASE_URL = '/api/proxy';

  let regulatoryData = {
    portal: {
      app: 'portal',
      name: '',
      version: '',
      medicalDevice: 'Medical Device',
      serialNumber: '',
      productionId: '',
      expirationDate: '',
      releaseDate: '',
      refNumber: '',
      instructionsUrl: '',
      instructionsUrlIos: '',
      instructionsUrlAndroid: '',
      phoneCompatibilityUrl: '',
      customerServiceUrl: '',
      intendedUse: '',
      manufacturer: {
        name: '',
        address1: '',
        address2: ''
      },
      euRepresentative: {
        name: '',
        address1: '',
        address2: ''
      }
    },
    ios: {
      app: 'ios',
      name: '',
      version: '',
      medicalDevice: 'Medical Device',
      serialNumber: '',
      productionId: '',
      expirationDate: '',
      releaseDate: '',
      refNumber: '',
      instructionsUrl: '',
      instructionsUrlIos: '',
      instructionsUrlAndroid: '',
      phoneCompatibilityUrl: '',
      customerServiceUrl: '',
      intendedUse: '',
      manufacturer: {
        name: '',
        address1: '',
        address2: ''
      },
      euRepresentative: {
        name: '',
        address1: '',
        address2: ''
      }
    },
    android: {
      app: 'android',
      name: '',
      version: '',
      medicalDevice: 'Medical Device',
      serialNumber: '',
      productionId: '',
      expirationDate: '',
      releaseDate: '',
      refNumber: '',
      instructionsUrl: '',
      instructionsUrlIos: '',
      instructionsUrlAndroid: '',
      phoneCompatibilityUrl: '',
      customerServiceUrl: '',
      intendedUse: '',
      manufacturer: {
        name: '',
        address1: '',
        address2: ''
      },
      euRepresentative: {
        name: '',
        address1: '',
        address2: ''
      }
    }
  };
  let isLoading = false;
  let isSaving = false;
  let saveMessage = '';
  let saveError = '';
  let activeTab = 'portal';

  onMount(async () => {
    selectedSite.set(null);
    selectedParticipant.set(null);
    await fetchRegulatoryData();
  });

  async function fetchRegulatoryData() {
    try {
      isLoading = true;
      saveMessage = '';
      saveError = '';

      const response = await fetch(`${API_BASE_URL}/regulatory/admin/all`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response error:', response.status, errorText);
        throw new Error(`Failed to fetch regulatory data: ${response.status} ${response.statusText}`);
      }

      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error('Server returned invalid JSON response');
      }

      if (result.success && result.data) {
        // Initialize data from database
        result.data.forEach(item => {
          if (regulatoryData[item.app]) {
            regulatoryData[item.app] = {
              app: item.app,
              name: item.name || '',
              version: item.version || '',
              medicalDevice: item.medicalDevice || 'Medical Device',
              serialNumber: item.serialNumber || '',
              productionId: item.productionId || '',
              expirationDate: item.expirationDate || '',
              releaseDate: item.releaseDate || '',
              refNumber: item.refNumber || '',
              instructionsUrl: item.instructionsUrl || '',
              instructionsUrlIos: item.instructionsUrlIos || '',
              instructionsUrlAndroid: item.instructionsUrlAndroid || '',
              phoneCompatibilityUrl: item.phoneCompatibilityUrl || '',
              customerServiceUrl: item.customerServiceUrl || '',
              intendedUse: item.intendedUse || '',
              manufacturer: {
                name: item.manufacturer?.name || '',
                address1: item.manufacturer?.address1 || '',
                address2: item.manufacturer?.address2 || ''
              },
              euRepresentative: {
                name: item.euRepresentative?.name || '',
                address1: item.euRepresentative?.address1 || '',
                address2: item.euRepresentative?.address2 || ''
              }
            };
          }
        });
      }
    } catch (err) {
      console.error('Error fetching regulatory data:', err);
      saveError = 'Failed to load regulatory data: ' + err.message;
    } finally {
      isLoading = false;
    }
  }

  async function saveRegulatoryData(app) {
    try {
      isSaving = true;
      saveMessage = '';
      saveError = '';

      // Ensure all required fields are present (even if empty strings)
      const appData = regulatoryData[app] || {};
      const manufacturerData = appData.manufacturer || {};
      const euRepData = appData.euRepresentative || {};
      
      // Explicitly construct the data object to ensure all fields are present
      const data = {
        name: String(appData.name ?? ''),
        version: String(appData.version ?? ''),
        medicalDevice: String(appData.medicalDevice ?? 'Medical Device'),
        serialNumber: String(appData.serialNumber ?? ''),
        productionId: String(appData.productionId ?? ''),
        expirationDate: String(appData.expirationDate ?? ''),
        releaseDate: String(appData.releaseDate ?? ''),
        refNumber: String(appData.refNumber ?? ''),
        instructionsUrl: String(appData.instructionsUrl ?? ''),
        instructionsUrlIos: String(appData.instructionsUrlIos ?? ''),
        instructionsUrlAndroid: String(appData.instructionsUrlAndroid ?? ''),
        phoneCompatibilityUrl: String(appData.phoneCompatibilityUrl ?? ''),
        customerServiceUrl: String(appData.customerServiceUrl ?? ''),
        manufacturer: {
          name: String(manufacturerData.name ?? ''),
          address1: String(manufacturerData.address1 ?? ''),
          address2: String(manufacturerData.address2 ?? '')
        },
        euRepresentative: {
          name: String(euRepData.name ?? ''),
          address1: String(euRepData.address1 ?? ''),
          address2: String(euRepData.address2 ?? '')
        },
        intendedUse: String(appData.intendedUse ?? '')
      };

      // Validate data structure before sending
      if (data.name === undefined || data.name === null) {
        console.error('ERROR: name field is undefined or null!', { appData, data });
        throw new Error('Data validation failed: name field is missing');
      }

      // Debug: Log the data being sent
      console.log(`Saving regulatory data for ${app}:`, JSON.stringify(data, null, 2));

      const response = await fetch(`${API_BASE_URL}/regulatory/${app}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorResult;
        try {
          errorResult = JSON.parse(errorText);
        } catch {
          errorResult = { message: errorText || 'Failed to save regulatory data' };
        }
        throw new Error(errorResult.message || 'Failed to save regulatory data');
      }

      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error('Server returned invalid JSON response');
      }

      if (result.success) {
        saveMessage = `Regulatory data for ${regulatoryData[app].name} saved successfully!`;
        setTimeout(() => {
          saveMessage = '';
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving regulatory data:', err);
      saveError = 'Failed to save: ' + err.message;
    } finally {
      isSaving = false;
    }
  }

  function setActiveTab(tab) {
    activeTab = tab;
    saveMessage = '';
    saveError = '';
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Regulatory Data Management" 
  description="Manage regulatory information for Summa Cloud, Summa iOS, and Summa Android applications. Update version numbers, serial numbers, manufacturer information, and intended use text."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Regulatory Data" itemCountTitle="Apps" itemCount={3}>
  <div class="flex items-center gap-4">
    <button 
      on:click={fetchRegulatoryData} 
      class="pagination mx-1 p-0" 
      disabled={isLoading}
      title="Refresh data"
    >
      <FontAwesomeIcon icon={faRotate} spin={isLoading} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex justify-center items-center py-8">
      <p class="text-gray-600">Loading regulatory data...</p>
    </div>
  {:else}
    <!-- Tabs -->
    <div class="border-b border-gray-300 dark:border-gray-600 mb-4">
      <div class="flex space-x-4">
        <button
          class="px-4 py-2 text-sm font-medium {activeTab === 'portal' 
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}"
          on:click={() => setActiveTab('portal')}
        >
          Summa Cloud
        </button>
        <button
          class="px-4 py-2 text-sm font-medium {activeTab === 'ios' 
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}"
          on:click={() => setActiveTab('ios')}
        >
          Summa iOS
        </button>
        <button
          class="px-4 py-2 text-sm font-medium {activeTab === 'android' 
            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}"
          on:click={() => setActiveTab('android')}
        >
          Summa Android
        </button>
      </div>
    </div>

    <!-- Messages -->
    {#if saveMessage}
      <div class="mb-4 p-3 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
        {saveMessage}
      </div>
    {/if}
    {#if saveError}
      <div class="mb-4 p-3 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded">
        {saveError}
      </div>
    {/if}

    <!-- Portal Tab -->
    {#if activeTab === 'portal'}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Summa Cloud Regulatory Information</h3>
        <div class="mb-4 text-xs">
          <!-- <a href="/regulatory?app=app" target="_blank" rel="noopener" class="text-blue-600 hover:underline mr-4">Open public view</a> -->
          <a href="/embed/regulatory?app=app" target="_blank" rel="noopener" class="text-blue-600 hover:underline">Open embed view</a>
        </div>
        <div class="space-y-4">
          <div>
            <label for="portal-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              id="portal-name"
              type="text"
              bind:value={regulatoryData.portal.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa Cloud"
            />
          </div>
          <div>
            <label for="portal-version" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Version</label>
            <input
              id="portal-version"
              type="text"
              bind:value={regulatoryData.portal.version}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2.3.1"
            />
          </div>
          <div>
            <label for="portal-medicalDevice" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medical Device (MD)</label>
            <input
              id="portal-medicalDevice"
              type="text"
              bind:value={regulatoryData.portal.medicalDevice}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Medical Device"
            />
          </div>
          <div>
            <label for="portal-serialNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Serial Number (Device Identifier)</label>
            <input
              id="portal-serialNumber"
              type="text"
              bind:value={regulatoryData.portal.serialNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(01) 00357599823009"
            />
          </div>
          <div>
            <label for="portal-productionId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Production Identifier</label>
            <input
              id="portal-productionId"
              type="text"
              bind:value={regulatoryData.portal.productionId}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(8012) 2.3.1.251103"
            />
          </div>
          <div>
            <label for="portal-expirationDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expiration Date</label>
            <input
              id="portal-expirationDate"
              type="text"
              bind:value={regulatoryData.portal.expirationDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2026-12-31"
            />
          </div>
          <div>
            <label for="portal-releaseDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Release Date</label>
            <input
              id="portal-releaseDate"
              type="text"
              bind:value={regulatoryData.portal.releaseDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2024-11-01"
            />
          </div>
          <div>
            <label for="portal-refNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference Number</label>
            <input
              id="portal-refNumber"
              type="text"
              bind:value={regulatoryData.portal.refNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="72156-01"
            />
          </div>
          <div>
            <label for="portal-instructionsUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Cloud URL</label>
            <input
              id="portal-instructionsUrl"
              type="text"
              bind:value={regulatoryData.portal.instructionsUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-cloud.pdf"
            />
          </div>
          <div>
            <label for="portal-instructionsUrlIos" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - iOS URL</label>
            <input
              id="portal-instructionsUrlIos"
              type="text"
              bind:value={regulatoryData.portal.instructionsUrlIos}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-ios.pdf"
            />
          </div>
          <div>
            <label for="portal-instructionsUrlAndroid" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Android URL</label>
            <input
              id="portal-instructionsUrlAndroid"
              type="text"
              bind:value={regulatoryData.portal.instructionsUrlAndroid}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-android.pdf"
            />
          </div>
          <div>
            <label for="portal-phoneCompatibilityUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Compatibility List URL</label>
            <input
              id="portal-phoneCompatibilityUrl"
              type="text"
              bind:value={regulatoryData.portal.phoneCompatibilityUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/phone-compatibility.pdf"
            />
          </div>
          <div>
            <label for="portal-customerServiceUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Service URL</label>
            <input
              id="portal-customerServiceUrl"
              type="text"
              bind:value={regulatoryData.portal.customerServiceUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/support"
            />
          </div>
          <div>
            <label for="portal-manufacturer-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Name</label>
            <input
              id="portal-manufacturer-name"
              type="text"
              bind:value={regulatoryData.portal.manufacturer.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Precision Digital Health Inc."
            />
          </div>
          <div>
            <label for="portal-manufacturer-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 1</label>
            <input
              id="portal-manufacturer-address1"
              type="text"
              bind:value={regulatoryData.portal.manufacturer.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="15615 Alton Parkway, Suite 450"
            />
          </div>
          <div>
            <label for="portal-manufacturer-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 2</label>
            <input
              id="portal-manufacturer-address2"
              type="text"
              bind:value={regulatoryData.portal.manufacturer.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Irvine, CA 92618 USA"
            />
          </div>
          <div>
            <label for="portal-euRep-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Name</label>
            <input
              id="portal-euRep-name"
              type="text"
              bind:value={regulatoryData.portal.euRepresentative.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="OBELIS S.A."
            />
          </div>
          <div>
            <label for="portal-euRep-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 1</label>
            <input
              id="portal-euRep-address1"
              type="text"
              bind:value={regulatoryData.portal.euRepresentative.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Boulevard Brand Whitlock 30,"
            />
          </div>
          <div>
            <label for="portal-euRep-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 2</label>
            <input
              id="portal-euRep-address2"
              type="text"
              bind:value={regulatoryData.portal.euRepresentative.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="1200 Brussels, Belgium"
            />
          </div>
          <div>
            <label for="portal-intendedUse" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Intended Use</label>
            <textarea
              id="portal-intendedUse"
              bind:value={regulatoryData.portal.intendedUse}
              rows="6"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa Cloud is intended for use by patients, caregivers and healthcare professionals..."
            ></textarea>
          </div>
          <div class="pt-4">
            <button
              on:click={() => saveRegulatoryData('portal')}
              disabled={isSaving}
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Saving...' : 'Save Portal Data'}
            </button>
          </div>
        </div>
      </div>
    {/if}

    <!-- iOS Tab -->
    {#if activeTab === 'ios'}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Summa iOS Regulatory Information</h3>
        <div class="mb-4 text-xs">
          <!-- <a href="/regulatory?app=ios" target="_blank" rel="noopener" class="text-blue-600 hover:underline mr-4">Open public view</a> -->
          <a href="/embed/regulatory?app=ios" target="_blank" rel="noopener" class="text-blue-600 hover:underline">Open embed view</a>
        </div>
        <div class="space-y-4">
          <div>
            <label for="ios-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              id="ios-name"
              type="text"
              bind:value={regulatoryData.ios.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa iOS"
            />
          </div>
          <div>
            <label for="ios-version" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Version</label>
            <input
              id="ios-version"
              type="text"
              bind:value={regulatoryData.ios.version}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2.3.1"
            />
          </div>
          <div>
            <label for="ios-medicalDevice" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medical Device (MD)</label>
            <input
              id="ios-medicalDevice"
              type="text"
              bind:value={regulatoryData.ios.medicalDevice}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Medical Device"
            />
          </div>
          <div>
            <label for="ios-serialNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Serial Number (Device Identifier)</label>
            <input
              id="ios-serialNumber"
              type="text"
              bind:value={regulatoryData.ios.serialNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(01) 00357599823010"
            />
          </div>
          <div>
            <label for="ios-productionId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Production Identifier</label>
            <input
              id="ios-productionId"
              type="text"
              bind:value={regulatoryData.ios.productionId}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(8012) 2.3.1.251103"
            />
          </div>
          <div>
            <label for="ios-expirationDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expiration Date</label>
            <input
              id="ios-expirationDate"
              type="text"
              bind:value={regulatoryData.ios.expirationDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2026-12-31"
            />
          </div>
          <div>
            <label for="ios-releaseDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Release Date</label>
            <input
              id="ios-releaseDate"
              type="text"
              bind:value={regulatoryData.ios.releaseDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2024-11-01"
            />
          </div>
          <div>
            <label for="ios-refNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference Number</label>
            <input
              id="ios-refNumber"
              type="text"
              bind:value={regulatoryData.ios.refNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="72156-02"
            />
          </div>
          <div>
            <label for="ios-instructionsUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Cloud URL</label>
            <input
              id="ios-instructionsUrl"
              type="text"
              bind:value={regulatoryData.ios.instructionsUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-cloud.pdf"
            />
          </div>
          <div>
            <label for="ios-instructionsUrlIos" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - iOS URL</label>
            <input
              id="ios-instructionsUrlIos"
              type="text"
              bind:value={regulatoryData.ios.instructionsUrlIos}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-ios.pdf"
            />
          </div>
          <div>
            <label for="ios-instructionsUrlAndroid" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Android URL</label>
            <input
              id="ios-instructionsUrlAndroid"
              type="text"
              bind:value={regulatoryData.ios.instructionsUrlAndroid}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-android.pdf"
            />
          </div>
          <div>
            <label for="ios-phoneCompatibilityUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Compatibility List URL</label>
            <input
              id="ios-phoneCompatibilityUrl"
              type="text"
              bind:value={regulatoryData.ios.phoneCompatibilityUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/phone-compatibility.pdf"
            />
          </div>
          <div>
            <label for="ios-customerServiceUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Service URL</label>
            <input
              id="ios-customerServiceUrl"
              type="text"
              bind:value={regulatoryData.ios.customerServiceUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/support"
            />
          </div>
          <div>
            <label for="ios-manufacturer-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Name</label>
            <input
              id="ios-manufacturer-name"
              type="text"
              bind:value={regulatoryData.ios.manufacturer.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Precision Digital Health Inc."
            />
          </div>
          <div>
            <label for="ios-manufacturer-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 1</label>
            <input
              id="ios-manufacturer-address1"
              type="text"
              bind:value={regulatoryData.ios.manufacturer.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="15615 Alton Parkway, Suite 450"
            />
          </div>
          <div>
            <label for="ios-manufacturer-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 2</label>
            <input
              id="ios-manufacturer-address2"
              type="text"
              bind:value={regulatoryData.ios.manufacturer.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Irvine, CA 92618 USA"
            />
          </div>
          <div>
            <label for="ios-euRep-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Name</label>
            <input
              id="ios-euRep-name"
              type="text"
              bind:value={regulatoryData.ios.euRepresentative.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="OBELIS S.A."
            />
          </div>
          <div>
            <label for="ios-euRep-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 1</label>
            <input
              id="ios-euRep-address1"
              type="text"
              bind:value={regulatoryData.ios.euRepresentative.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Boulevard Brand Whitlock 30,"
            />
          </div>
          <div>
            <label for="ios-euRep-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 2</label>
            <input
              id="ios-euRep-address2"
              type="text"
              bind:value={regulatoryData.ios.euRepresentative.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="1200 Brussels, Belgium"
            />
          </div>
          <div>
            <label for="ios-intendedUse" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Intended Use</label>
            <textarea
              id="ios-intendedUse"
              bind:value={regulatoryData.ios.intendedUse}
              rows="6"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa iOS is intended for use by patients, caregivers and healthcare professionals..."
            ></textarea>
          </div>
          <div class="pt-4">
            <button
              on:click={() => saveRegulatoryData('ios')}
              disabled={isSaving}
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Saving...' : 'Save iOS Data'}
            </button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Android Tab -->
    {#if activeTab === 'android'}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Summa Android Regulatory Information</h3>
        <div class="mb-4 text-xs">
          <!-- <a href="/regulatory?app=android" target="_blank" rel="noopener" class="text-blue-600 hover:underline mr-4">Open public view</a> -->
          <a href="/embed/regulatory?app=android" target="_blank" rel="noopener" class="text-blue-600 hover:underline">Open embed view</a>
        </div>
        <div class="space-y-4">
          <div>
            <label for="android-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              id="android-name"
              type="text"
              bind:value={regulatoryData.android.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa Android"
            />
          </div>
          <div>
            <label for="android-version" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Version</label>
            <input
              id="android-version"
              type="text"
              bind:value={regulatoryData.android.version}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2.3.1"
            />
          </div>
          <div>
            <label for="android-medicalDevice" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medical Device (MD)</label>
            <input
              id="android-medicalDevice"
              type="text"
              bind:value={regulatoryData.android.medicalDevice}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Medical Device"
            />
          </div>
          <div>
            <label for="android-serialNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Serial Number (Device Identifier)</label>
            <input
              id="android-serialNumber"
              type="text"
              bind:value={regulatoryData.android.serialNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(01) 00357599823011"
            />
          </div>
          <div>
            <label for="android-productionId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Production Identifier</label>
            <input
              id="android-productionId"
              type="text"
              bind:value={regulatoryData.android.productionId}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="(8012) 2.3.1.251103"
            />
          </div>
          <div>
            <label for="android-expirationDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expiration Date</label>
            <input
              id="android-expirationDate"
              type="text"
              bind:value={regulatoryData.android.expirationDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2026-12-31"
            />
          </div>
          <div>
            <label for="android-releaseDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Release Date</label>
            <input
              id="android-releaseDate"
              type="text"
              bind:value={regulatoryData.android.releaseDate}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="2024-11-01"
            />
          </div>
          <div>
            <label for="android-refNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference Number</label>
            <input
              id="android-refNumber"
              type="text"
              bind:value={regulatoryData.android.refNumber}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="72156-03"
            />
          </div>
          <div>
            <label for="android-instructionsUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Cloud URL</label>
            <input
              id="android-instructionsUrl"
              type="text"
              bind:value={regulatoryData.android.instructionsUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-cloud.pdf"
            />
          </div>
          <div>
            <label for="android-instructionsUrlIos" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - iOS URL</label>
            <input
              id="android-instructionsUrlIos"
              type="text"
              bind:value={regulatoryData.android.instructionsUrlIos}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-ios.pdf"
            />
          </div>
          <div>
            <label for="android-instructionsUrlAndroid" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions for Use - Android URL</label>
            <input
              id="android-instructionsUrlAndroid"
              type="text"
              bind:value={regulatoryData.android.instructionsUrlAndroid}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/ifu-android.pdf"
            />
          </div>
          <div>
            <label for="android-phoneCompatibilityUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Compatibility List URL</label>
            <input
              id="android-phoneCompatibilityUrl"
              type="text"
              bind:value={regulatoryData.android.phoneCompatibilityUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/phone-compatibility.pdf"
            />
          </div>
          <div>
            <label for="android-customerServiceUrl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Service URL</label>
            <input
              id="android-customerServiceUrl"
              type="text"
              bind:value={regulatoryData.android.customerServiceUrl}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="https://precisiondigitalhealth.com/support"
            />
          </div>
          <div>
            <label for="android-manufacturer-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Name</label>
            <input
              id="android-manufacturer-name"
              type="text"
              bind:value={regulatoryData.android.manufacturer.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Precision Digital Health Inc."
            />
          </div>
          <div>
            <label for="android-manufacturer-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 1</label>
            <input
              id="android-manufacturer-address1"
              type="text"
              bind:value={regulatoryData.android.manufacturer.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="15615 Alton Parkway, Suite 450"
            />
          </div>
          <div>
            <label for="android-manufacturer-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manufacturer Address Line 2</label>
            <input
              id="android-manufacturer-address2"
              type="text"
              bind:value={regulatoryData.android.manufacturer.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Irvine, CA 92618 USA"
            />
          </div>
          <div>
            <label for="android-euRep-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Name</label>
            <input
              id="android-euRep-name"
              type="text"
              bind:value={regulatoryData.android.euRepresentative.name}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="OBELIS S.A."
            />
          </div>
          <div>
            <label for="android-euRep-address1" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 1</label>
            <input
              id="android-euRep-address1"
              type="text"
              bind:value={regulatoryData.android.euRepresentative.address1}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Boulevard Brand Whitlock 30,"
            />
          </div>
          <div>
            <label for="android-euRep-address2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EU Representative Address Line 2</label>
            <input
              id="android-euRep-address2"
              type="text"
              bind:value={regulatoryData.android.euRepresentative.address2}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="1200 Brussels, Belgium"
            />
          </div>
          <div>
            <label for="android-intendedUse" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Intended Use</label>
            <textarea
              id="android-intendedUse"
              bind:value={regulatoryData.android.intendedUse}
              rows="6"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Summa Android is intended for use by patients, caregivers and healthcare professionals..."
            ></textarea>
          </div>
          <div class="pt-4">
            <button
              on:click={() => saveRegulatoryData('android')}
              disabled={isSaving}
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Saving...' : 'Save Android Data'}
            </button>
          </div>
        </div>
      </div>
    {/if}

  {/if}
</div>

